// Pure hulpfuncties voor importeren, exporteren en delen (port van legacy handleUploadedFile,
// processImportedActivities, exportActivitiesAsJson en openShareImportModal).
import {
  countNewActivities,
  formatValidationErrors,
  MAX_IMPORT_ITEMS,
  MAX_TOTAL_ACTIVITIES,
  parseMarkdownTable,
  sanitizeActivityList,
  validateActivities,
} from '../../domain/activities';
import type { Activity, ImportMode } from '../../domain/types';

export const MAX_UPLOAD_BYTES = 2 * 1024 * 1024;

export type ImportFileKind = 'json' | 'markdown';
export type StatusKind = 'info' | 'success' | 'error';

export interface StatusMessage {
  kind: StatusKind;
  text: string;
}

/** .json (of application/json) = backup; .md/.markdown/.txt = Markdown-tabel; anders null. */
export function detectImportFileKind(name: string, mime = ''): ImportFileKind | null {
  const lower = String(name || '').toLowerCase();
  if (lower.endsWith('.json') || mime === 'application/json') return 'json';
  if (lower.endsWith('.md') || lower.endsWith('.markdown') || lower.endsWith('.txt')) return 'markdown';
  return null;
}

/** Controleert type en grootte vóór het inlezen. Retourneert een foutmelding of null. */
export function checkImportFile(name: string, mime: string, size: number): StatusMessage | null {
  if (!detectImportFileKind(name, mime)) {
    return { kind: 'error', text: 'Kies een .json-backup of een .md-bestand met een activiteitentabel.' };
  }
  if (size > MAX_UPLOAD_BYTES) return { kind: 'error', text: 'Dit bestand is te groot (maximaal 2 MB).' };
  return null;
}

export type ParsedImport = { ok: true; items: unknown[] } | { ok: false; message: StatusMessage };

/** Leest de tekst van een bestand: JSON wordt tegen het schema gevalideerd, Markdown als tabel geparsed. */
export function parseImportText(kind: ImportFileKind, text: string): ParsedImport {
  const fail = (t: string): ParsedImport => ({ ok: false, message: { kind: 'error', text: t } });
  let items: unknown[];
  if (kind === 'json') {
    let parsed: unknown;
    try {
      parsed = JSON.parse(text.replace(/^﻿/, ''));
    } catch {
      return fail('Dit is geen geldig JSON-bestand. Controleer of het bestand niet beschadigd is.');
    }
    const result = validateActivities(parsed);
    if (!result.ok) {
      return fail(`De backup voldoet niet aan het GameOn-formaat:\n${formatValidationErrors(result.errors)}`);
    }
    items = parsed as unknown[];
  } else {
    items = parseMarkdownTable(text);
    if (items.length > MAX_IMPORT_ITEMS) {
      return fail(`Te veel activiteiten in één bestand (${items.length}; maximaal ${MAX_IMPORT_ITEMS}).`);
    }
  }
  if (items.length === 0) return fail('Geen geldige activiteiten gevonden. Controleer het formaat.');
  return { ok: true, items };
}

export type ImportPlan =
  | { ok: true; raw: unknown[]; mode: ImportMode; successText: string }
  | { ok: false; message: StatusMessage };

/**
 * Bepaalt vooraf of een import kan en welke melding erbij hoort.
 * Samenvoegen: dubbele namen worden overgeslagen; meer dan MAX_TOTAL_ACTIVITIES wordt geweigerd.
 */
export function planImport(current: Activity[], raw: unknown[], mode: ImportMode): ImportPlan {
  const capped = Array.isArray(raw) ? raw.slice(0, MAX_IMPORT_ITEMS) : [];
  const incoming = sanitizeActivityList(capped);
  if (incoming.length === 0) {
    return { ok: false, message: { kind: 'error', text: 'Geen geldige activiteiten gevonden.' } };
  }
  if (mode === 'overwrite') {
    return {
      ok: true,
      raw: capped,
      mode,
      successText: `${incoming.length} activiteiten ingeladen. De vorige lijst is vervangen.`,
    };
  }
  const fresh = countNewActivities(current, incoming);
  const skipped = incoming.length - fresh;
  if (fresh === 0) {
    return {
      ok: false,
      message: { kind: 'info', text: `Geen nieuwe activiteiten: alle ${incoming.length} staan al in je lijst.` },
    };
  }
  if (current.length + fresh > MAX_TOTAL_ACTIVITIES) {
    return {
      ok: false,
      message: {
        kind: 'error',
        text: `Samenvoegen zou meer dan ${MAX_TOTAL_ACTIVITIES} activiteiten opleveren. Kies "Bestaande gegevens overschrijven" of verklein het bestand.`,
      },
    };
  }
  return {
    ok: true,
    raw: capped,
    mode,
    successText: `${fresh} nieuwe activiteiten toegevoegd${skipped > 0 ? ` (${skipped} dubbele overgeslagen)` : ''}.`,
  };
}

export const SAVE_FAILED_TEXT = 'Opslaan mislukt. Je bestaande activiteiten zijn ongewijzigd gebleven.';

/** `gameon_activiteiten_YYYY-MM-DD.json` in lokale tijd. */
export function exportFileName(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `gameon_activiteiten_${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}.json`;
}

/** Voorbeeldregels voor de deel-importmodal: "Naam (30 min)" + aantal overige. */
export function sharePreview(list: Activity[], max = 5): { lines: string[]; more: number } {
  return {
    lines: list.slice(0, max).map((a) => `${a.naam} (${Number(a.duur)} min)`),
    more: Math.max(0, list.length - max),
  };
}

/** Haalt een voorloop-emoji (bijv. "⚠️ ") weg: de UI toont zelf een lucide-icoon of niets. */
export function stripLeadingEmoji(text: string): string {
  return String(text).replace(/^(?:\p{Extended_Pictographic}|️|‍)+\s*/u, '');
}
